import { Link } from 'react-router-dom';
import { Package, Search, User, LogOut, Mail, Info } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useState } from 'react';
import toast from 'react-hot-toast';

interface NavbarProps {
  onSearch?: (query: string) => void;
}

export default function Navbar({ onSearch }: NavbarProps) {
  const { user, openAuthModal, logout } = useAuth();
  const [showInfo, setShowInfo] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleContactClick = () => {
    toast.success(
      <div className="text-center">
        <p className="font-medium mb-1">Coming Soon! 🎉</p>
        <p className="text-sm">We're excited to hear from you! Our contact system is being enhanced to serve you better.</p>
        <p className="text-xs mt-1 text-gray-500">Stay tuned for updates.</p>
      </div>,
      {
        duration: 5000,
        icon: '✉️'
      }
    );
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    onSearch?.(query);
  };

  return (
    <nav className="bg-white shadow-lg">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center space-x-2">
            <Package className="w-8 h-8 text-indigo-600" />
            <span className="text-xl font-bold text-gray-800">ExtensionHub</span>
          </Link>

          <div className="flex-1 max-w-xl mx-8">
            <div className="relative">
              <input
                type="text"
                placeholder="Search extensions..."
                value={searchQuery}
                onChange={handleSearch}
                className="w-full px-4 py-2 pl-10 pr-4 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <button
              onClick={() => setShowInfo(!showInfo)}
              className="text-gray-600 hover:text-indigo-600 transition-colors relative"
              title="About ExtensionHub"
            >
              <Info className="w-5 h-5" />
              {showInfo && (
                <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-xl p-6 text-sm text-gray-600 z-50 border border-gray-100">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-indigo-600">ExtensionHub</h3>
                    <span className="px-2 py-1 bg-indigo-100 text-indigo-800 text-xs rounded-full">v25.1 Beta</span>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium text-gray-900 mb-1">About</h4>
                      <p className="text-gray-600">
                        An innovative educational platform designed to enhance teaching and learning through customizable extensions.
                      </p>
                    </div>

                    <div>
                      <h4 className="font-medium text-gray-900 mb-1">Developer</h4>
                      <p className="text-gray-600">
                        Created by Teacher Mohammad Awwad, dedicated to advancing educational technology.
                      </p>
                    </div>

                    <div>
                      <h4 className="font-medium text-gray-900 mb-1">Features</h4>
                      <ul className="text-gray-600 list-disc list-inside">
                        <li>Secure extension marketplace</li>
                        <li>Educational tools and resources</li>
                        <li>Teacher-student collaboration</li>
                        <li>Custom learning experiences</li>
                      </ul>
                    </div>

                    <div className="pt-2 border-t border-gray-100">
                      <p className="text-xs text-gray-500">
                        © 2024 Teacher Mohammad Awwad. All rights reserved.
                        <br />
                        <span className="mt-1 block">
                          This software is protected by international copyright laws and treaties.
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </button>
            <button
              onClick={handleContactClick}
              className="text-gray-600 hover:text-indigo-600 transition-colors group relative"
              title="Contact Us"
            >
              <Mail className="w-5 h-5" />
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-indigo-500 rounded-full group-hover:animate-ping"></span>
            </button>
            {user ? (
              <div className="flex items-center space-x-4">
                {user.role === 'admin' && (
                  <Link
                    to="/admin"
                    className="text-gray-600 hover:text-indigo-600 transition-colors"
                    title="Admin Dashboard"
                  >
                    <User className="w-5 h-5" />
                  </Link>
                )}
                <Link
                  to="/profile"
                  className="flex items-center space-x-2 text-gray-700 hover:text-indigo-600"
                  title={user.displayName || 'Profile'}
                >
                  {user.photoURL ? (
                    <img
                      src={user.photoURL}
                      alt={user.displayName || 'User'}
                      className="w-8 h-8 rounded-full"
                    />
                  ) : (
                    <User className="w-8 h-8 p-1 rounded-full bg-gray-100" />
                  )}
                  <span className="hidden md:block">{user.displayName}</span>
                </Link>
                <button
                  onClick={handleLogout}
                  className="text-gray-600 hover:text-indigo-600 transition-colors"
                  title="Logout"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <button
                onClick={openAuthModal}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Sign In
              </button>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}